import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { sileo } from 'sileo'
import { useGlobalContext } from '../context/GlobalState'
import { BusinessAPI } from '../api/business.api'
import Layout from '../components/layouts/index'
import Tabs from '../components/modules/tabs/Tabs'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import MercadoPagoAccountConfirmModal from '../components/modules/views/dashboard/Ajustes/Integraciones/MercadoPagoAccountConfirmModal'
import { sanitizeRut } from '../components/modules/views/dashboard/Facturacion/utils/sanitizeRut'

const MercadoPagoCallback = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentBusiness, setCurrentBusiness } = useGlobalContext()
  const businessAPI = new BusinessAPI()
  const navigate = useNavigate()
  const [status, setStatus] = useState('processing')
  const [modalOpen, setModalOpen] = useState(false)
  const [accountInfo, setAccountInfo] = useState(null)
  const [accountName, setAccountName] = useState('')
  const [resolvedBusiness, setResolvedBusiness] = useState(null)
  const previewStartedRef = useRef(false)
  const mpAccountIdRef = useRef(null)
  const bankAccountIdRef = useRef(null)

  useEffect(() => {
    const code = searchParams.get('code')
    const businessIdParam = searchParams.get('businessId')
    const mpAccountId = searchParams.get('mpAccountId')
    const bankAccountId = searchParams.get('bankAccountId')

    if (!code || previewStartedRef.current) return

    mpAccountIdRef.current = mpAccountId || null
    bankAccountIdRef.current = bankAccountId ? Number(bankAccountId) : null

    previewStartedRef.current = true
    setSearchParams({}, { replace: true })

    const resolveAndProcess = async () => {
      let business = currentBusiness

      if (businessIdParam) {
        try {
          const res = await businessAPI.getBusinessById(Number(businessIdParam))
          business = res.data
          setCurrentBusiness(business)
        } catch (err) {
          console.error('Error fetching business for OAuth callback:', err)
        }
      }

      if (!business?.id || !business?.organizationId) {
        previewStartedRef.current = false
        return
      }

      setResolvedBusiness(business)

      try {
        const response = await businessAPI.previewMercadoPagoOAuth(
          business.organizationId,
          business.id,
          code,
          mpAccountIdRef.current
        )

        if (response?.config?.method?.toLowerCase() === 'options') return

        if (response?.status === 200 || response?.status === 201) {
          const data = response.data
          const businessRut = business?.rut ? sanitizeRut(business.rut) : null
          const mpRut = data?.identification?.number ? sanitizeRut(data.identification.number) : null
          const rutMatch = !businessRut || !mpRut ? true : businessRut === mpRut
          setAccountInfo({ ...data, rutMatch })
          setModalOpen(true)
        }
      } catch (error) {
        console.error('Error handling Mercado Pago OAuth callback:', error)
        if (error.config?.method?.toLowerCase() === 'options') return

        if (error.response?.status === 400) {
          setStatus('errado')
        }
      }
    }

    resolveAndProcess()
  }, [searchParams, currentBusiness, setSearchParams, setCurrentBusiness])

  const handleConfirm = async () => {
    const trimmedName = accountName.trim()
    if (!trimmedName) return

    setModalOpen(false)
    const businessId = resolvedBusiness?.id ?? currentBusiness?.id
    try {
      await businessAPI.confirmMercadoPagoOAuth(businessId, mpAccountIdRef.current, trimmedName, bankAccountIdRef.current ?? undefined)
      sileo.success({
        title: 'Conexión Exitosa',
        description: (
          <span className='text-green-500/80 font-medium'>
            Mercado Pago ha sido conectado exitosamente. Tus transacciones estarán en Luca en
            unos minutos
          </span>
        )
      })
      setStatus('success')
      setTimeout(() => {
        navigate('/administracion')
      }, 2000)
    } catch (error) {
      console.error('Error confirming Mercado Pago OAuth:', error)
      setStatus('errado')
    }
  }

  const handleCancel = async () => {
    setModalOpen(false)
    setAccountName('')
    const businessId = resolvedBusiness?.id ?? currentBusiness?.id
    try {
      await businessAPI.cancelMercadoPagoOAuth(businessId, mpAccountIdRef.current)
    } catch (error) {
      console.error('Error cancelling Mercado Pago OAuth:', error)
    }
    setStatus('cancelled')
  }

  const tabs = [
    {
      key: 'mercadopago',
      title: 'Mercado Pago',
      content: (
        <div className='flex flex-col items-center justify-center min-h-[400px] py-12'>
          {status === 'processing' && (
            <div className='text-center'>
              <div className='inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-accent1 mb-6'></div>
              <h2 className='text-2xl font-semibold text-gray-800 mb-2'>
                Procesando integración...
              </h2>
              <p className='text-gray-600'>
                Por favor espera mientras completamos la integración con Mercado Pago
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className='text-center'>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: '#10b981',
                  marginBottom: '24px'
                }}
              />
              <h2 className='text-3xl font-bold text-gray-800 mb-4'>
                Mercado Pago integrado correctamente
              </h2>
              <p className='text-lg text-gray-600 mb-6'>
                Tu cuenta de Mercado Pago ha sido vinculada exitosamente
              </p>
            </div>
          )}

          {status === 'cancelled' && (
            <div className='text-center'>
              <ErrorIcon
                sx={{
                  fontSize: 80,
                  color: '#f59e0b',
                  marginBottom: '24px'
                }}
              />
              <h2 className='text-3xl font-bold text-gray-800 mb-4'>
                Integración cancelada
              </h2>
              <p className='text-lg text-gray-600 mb-6'>
                No se completó la integración con Mercado Pago. Si deseas conectar otra cuenta,
                asegúrate de cerrar sesión en Mercado Pago antes de intentarlo nuevamente.
              </p>
            </div>
          )}

          {status === 'errado' && (
            <div className='text-center'>
              <ErrorIcon
                sx={{
                  fontSize: 80,
                  color: '#ef4444',
                  marginBottom: '24px'
                }}
              />
              <h2 className='text-3xl font-bold text-gray-800 mb-4'>
                No se pudo completar la integración
              </h2>
              <p className='text-lg text-gray-600 mb-6'>
                Hubo un error al intentar vincular tu cuenta de Mercado Pago. Por favor, intenta
                nuevamente.
              </p>
            </div>
          )}
        </div>
      )
    }
  ]

  return (
    <Layout className='px-4 md:px-8'>
      <Tabs tabs={tabs} />
      <MercadoPagoAccountConfirmModal
        open={modalOpen}
        accountInfo={accountInfo}
        loading={false}
        error={false}
        accountName={accountName}
        onAccountNameChange={setAccountName}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Layout>
  )
}

export default MercadoPagoCallback
